import React, { useMemo, useLayoutEffect, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLayout } from '../../contexts/LayoutContext';
import { modalSizes, modalHeights } from '../../theme/modalSizes';
import { Z_INDEX } from '../../constants';

const CSS_VARS = {
  headerHeight: '--header-height',
  leftSidebar: '--left-sidebar-width',
  rightSidebar: '--right-sidebar-width',
};

// Add debug helper
const logModalState = (panelRef, isDarkMode, isOpen) => {
  if (!panelRef.current) return;

  const panel = panelRef.current;
  const overlay = panel.closest('.fixed.inset-0');
  const container = panel.closest('.grid.place-items-center');

  console.group('🎭 [Modal] Debug State');
  console.log('Theme & State:', { isDarkMode, isOpen });

  // Log CSS Variables
  const rootStyle = getComputedStyle(document.documentElement);
  console.group('CSS Variables');
  Object.values(CSS_VARS).forEach(varName => {
    console.log(varName + ':', rootStyle.getPropertyValue(varName));
  });
  console.groupEnd();

  // Log Panel Styles
  const panelStyle = getComputedStyle(panel);
  console.group('Panel Styles');
  console.log('position:', panelStyle.position);
  console.log('zIndex:', panelStyle.zIndex);
  console.log('transform:', panelStyle.transform);
  console.log('width:', panelStyle.width);
  console.log('maxWidth:', panelStyle.maxWidth);
  console.log('maxHeight:', panelStyle.maxHeight);
  console.log('visibility:', panelStyle.visibility);
  console.log('pointerEvents:', panelStyle.pointerEvents);
  console.groupEnd();

  // Log Overlay Styles
  if (overlay) {
    const overlayStyle = getComputedStyle(overlay);
    console.group('Overlay Styles');
    console.log('background:', overlayStyle.background);
    console.log('zIndex:', overlayStyle.zIndex);
    console.log('pointerEvents:', overlayStyle.pointerEvents);
    console.groupEnd();
  }

  // Log Container Styles
  if (container) {
    const containerStyle = getComputedStyle(container);
    console.group('Container Styles');
    console.log('position:', containerStyle.position);
    console.log('padding:', {
      top: containerStyle.paddingTop,
      right: containerStyle.paddingRight,
      bottom: containerStyle.paddingBottom,
      left: containerStyle.paddingLeft
    });
    console.log('transform:', containerStyle.transform);
    console.groupEnd();
  }

  // Log Measurements
  const rect = panel.getBoundingClientRect();
  console.group('Measurements');
  console.log('BoundingClientRect:', {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    left: rect.left
  });
  console.log('Viewport:', {
    width: window.innerWidth,
    height: window.innerHeight
  });
  console.groupEnd();

  console.groupEnd();
};

export default function Modal({
  isOpen,
  onClose,
  width = 'medium',
  maxHeight: maxHeightToken = 'dynamic',
  padding = '1rem',
  closeOnOverlayClick = true,
  children,
  className = '',
  backgroundClassName = '',
}) {
  const { isDarkMode } = useTheme();
  const panelRef = useRef(null);

  // Debug on mount, theme change, and open state change
  useEffect(() => {
    if (!isOpen) return;
    
    // Log initial state
    logModalState(panelRef, isDarkMode, isOpen);

    // Log after a short delay to catch any async updates
    const timeoutId = setTimeout(() => {
      logModalState(panelRef, isDarkMode, isOpen);
      // Force a resize event to see if it fixes layout
      window.dispatchEvent(new Event('resize'));
      // Log one more time after resize
      setTimeout(() => logModalState(panelRef, isDarkMode, isOpen), 50);
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [isDarkMode, isOpen]);

  // Resolve modal width token or literal
  const resolvedWidth = useMemo(() => {
    const rw = modalSizes[width] || width;
    console.log('[Modal][resolvedWidth]', rw);
    return rw;
  }, [width]);

  // Compute max-height: 90% of viewport minus header
  const resolvedMaxHeight = useMemo(() => {
    let mh;
    if (maxHeightToken in modalHeights) {
      mh = modalHeights[maxHeightToken];
    } else if (typeof window === 'undefined') {
      mh = '90vh';
    } else {
      mh = `calc(90vh - var(${CSS_VARS.headerHeight}))`;
    }
    console.log('[Modal][resolvedMaxHeight]', mh);
    return mh;
  }, [maxHeightToken]);

  if (!isOpen) return null;

  return (
    <Dialog
      static
      open={isOpen}
      onClose={closeOnOverlayClick ? onClose : () => {}}
      className="relative z-[1400]"
    >
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0
          ${isDarkMode ? 'bg-black/40' : 'bg-black/30'}
          pointer-events-auto
          cursor-not-allowed
          z-[1400]
        `}
        onClick={closeOnOverlayClick ? onClose : (e) => e.stopPropagation()}
      />

      {/* Centering container */}
      <div className="fixed inset-0 grid place-items-center pointer-events-none z-[1400]">
        <div 
          className="w-full h-full grid place-items-center"
          style={{
            paddingTop: `var(${CSS_VARS.headerHeight})`,
            paddingLeft: `var(${CSS_VARS.leftSidebar})`,
            paddingRight: `var(${CSS_VARS.rightSidebar})`,
          }}
        >
          <Dialog.Panel
            ref={panelRef}
            style={{
              width: resolvedWidth,
              maxWidth: `min(${typeof resolvedWidth === 'string' ? resolvedWidth : resolvedWidth + 'px'}, calc(100% - 2rem))`,
              maxHeight: resolvedMaxHeight,
              padding,
            }}
            className={[
              'pointer-events-auto overflow-auto rounded-lg shadow-xl transition-all z-[1401]',
              backgroundClassName || (isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'),
              className,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {children}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
